/**
 * STORY-STRIPE-002 — In-App Billing Summary Card
 *
 * Surfaces the rich billing data Stripe holds (plan, next billing date,
 * payment method, recent invoices) without forcing the user out to the
 * Customer Portal. Drops into IndividualBilling and AffiliateBilling.
 *
 * Backend: GET /individuals/billing/summary and /affiliates/billing/summary,
 * both cached 60s in KV.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useApi } from '@/hooks/useApi';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, Download, ExternalLink, FileText, Loader2 } from 'lucide-react';

interface BillingSummary {
  status: string;
  plan: {
    nickname: string | null;
    amount_cents: number | null;
    currency: string | null;
    interval: 'day' | 'week' | 'month' | 'year' | null;
  } | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  trial_end: number | null;
  pause: {
    resumes_at: number | null;
    behavior: 'keep_as_draft' | 'mark_uncollectible' | 'void';
  } | null;
  payment_method: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  } | null;
  invoices: Array<{
    id: string;
    number: string | null;
    amount_paid_cents: number;
    currency: string;
    status: string;
    created: number;
    hosted_invoice_url: string | null;
    invoice_pdf: string | null;
  }>;
}

interface Props {
  scope: 'individual' | 'affiliate';
  affiliateId?: string;
}

function formatCents(cents: number | null, currency: string | null) {
  if (cents === null || !currency) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function formatUnix(timestamp: number | null) {
  if (!timestamp) return '—';
  return new Date(timestamp * 1000).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatInvoiceStatus(status: string) {
  switch (status) {
    case 'paid':
      return { label: 'Paid', color: 'bg-green-500/10 text-green-500 border-green-500/20' };
    case 'open':
      return { label: 'Open', color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' };
    case 'uncollectible':
      return { label: 'Uncollectible', color: 'bg-red-500/10 text-red-500 border-red-500/20' };
    case 'void':
      return { label: 'Void', color: 'bg-gray-500/10 text-gray-500 border-gray-500/20' };
    case 'draft':
      return { label: 'Draft', color: 'bg-gray-500/10 text-gray-500 border-gray-500/20' };
    default:
      return { label: status, color: 'bg-gray-500/10 text-gray-500 border-gray-500/20' };
  }
}

export function BillingSummaryCard({ scope, affiliateId }: Props) {
  const { api } = useApi();

  const endpoint =
    scope === 'individual'
      ? '/individuals/billing/summary'
      : affiliateId
        ? `/affiliates/billing/summary?affiliate_id=${encodeURIComponent(affiliateId)}`
        : '/affiliates/billing/summary';

  const { data, isLoading, error } = useQuery({
    queryKey: ['billing-summary', scope, affiliateId ?? null],
    queryFn: async () => {
      const response = await api.get<BillingSummary>(endpoint);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    staleTime: 60_000, // match backend KV TTL
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-destructive">
          Unable to load billing summary right now. Open the Customer Portal below to view your full
          billing history.
        </CardContent>
      </Card>
    );
  }

  if (!data || data.status === 'no_subscription') {
    return null; // Parent page already handles the "subscribe now" state.
  }

  const isTrialing = data.status === 'trialing';
  const isActiveOrTrialing = data.status === 'active' || isTrialing;
  const isPaused = data.pause !== null;

  return (
    <Card className="animate-fade-in">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Billing Summary
        </CardTitle>
        <CardDescription>
          Your plan, next bill, payment method, and recent invoices.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* STORY-STRIPE-004 — Pause state banner */}
        {isPaused && (
          <div className="rounded-md border border-orange-500/30 bg-orange-500/5 p-4">
            <p className="text-sm font-semibold text-orange-500">Your subscription is paused</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {data.pause?.resumes_at
                ? `Billing auto-resumes on ${formatUnix(data.pause.resumes_at)}.`
                : 'Billing is paused indefinitely. Resume from the Customer Portal below.'}{' '}
              Your existing access continues through the paid period.
            </p>
          </div>
        )}

        {/* Plan + next billing */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Current Plan</p>
            <p className="mt-1 text-lg font-semibold">
              {data.plan?.nickname ?? 'ErgShoot Subscription'}
            </p>
            <p className="text-sm text-muted-foreground">
              {data.plan
                ? `${formatCents(data.plan.amount_cents, data.plan.currency)} / ${data.plan.interval ?? 'month'}`
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {data.cancel_at_period_end
                ? 'Access ends'
                : isTrialing
                  ? 'Trial ends'
                  : 'Next billing date'}
            </p>
            <p className="mt-1 text-lg font-semibold">
              {formatUnix(isTrialing ? data.trial_end : data.current_period_end)}
            </p>
            {data.cancel_at_period_end && (
              <p className="text-xs text-orange-500">Subscription will not renew.</p>
            )}
            {isTrialing && !data.cancel_at_period_end && (
              <p className="text-xs text-muted-foreground">Billing starts after your trial ends.</p>
            )}
          </div>
        </div>

        {/* Payment method */}
        <div className="rounded-md border border-border p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Payment Method</p>
                {data.payment_method ? (
                  <p className="text-xs text-muted-foreground">
                    {data.payment_method.brand.toUpperCase()} •••• {data.payment_method.last4} —
                    expires {String(data.payment_method.exp_month).padStart(2, '0')}/
                    {String(data.payment_method.exp_year).slice(-2)}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {isTrialing
                      ? 'No card on file — add one to keep access after your trial.'
                      : 'No card on file.'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Invoice history */}
        {data.invoices.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
              Recent invoices
            </p>
            <div className="rounded-md border border-border divide-y divide-border">
              {data.invoices.map((invoice) => {
                const statusBadge = formatInvoiceStatus(invoice.status);
                return (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between px-4 py-3 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{invoice.number ?? invoice.id}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatUnix(invoice.created)} —{' '}
                        {formatCents(invoice.amount_paid_cents, invoice.currency)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className={statusBadge.color}>
                        {statusBadge.label}
                      </Badge>
                      {invoice.invoice_pdf && (
                        <Button
                          asChild
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          title="Download PDF"
                        >
                          <a href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer">
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      {invoice.hosted_invoice_url && (
                        <Button
                          asChild
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          title="View invoice"
                        >
                          <a
                            href={invoice.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!isActiveOrTrialing && data.status === 'past_due' && (
          <p className="text-xs text-orange-500">
            Your last payment failed. Use "Manage Subscription" below to update your card.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
